import React, { useState, useRef, useEffect } from 'react';
import { X, ThumbsUp, ThumbsDown } from 'lucide-react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment: string) => void;
  initialRating: number;
}

function FeedbackModal({ isOpen, onClose, onSubmit, initialRating }: FeedbackModalProps) {
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(initialRating);
  const modalRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      // Focus the comment textarea when modal opens
      commentRef.current?.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      aria-modal="true"
      role="dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        ref={modalRef}
        className="bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 max-w-md w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute -right-2 -top-2 w-12 h-12 flex items-center justify-center rounded-xl hover:bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-all duration-300 backdrop-blur-sm"
          aria-label="Close feedback modal"
        >
          <div className="bg-slate-900/90 w-8 h-8 rounded-lg flex items-center justify-center">
            <X className="w-5 h-5" />
          </div>
        </button>
        
        <div className="mb-6">
          <h3 className="text-xl font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]">
            Provide Feedback
          </h3>
          <p className="text-sm bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            Help us improve Ask Rumi
          </p>
        </div>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-3">
              How helpful was this response?
            </label>
            <div className="flex justify-center space-x-8">
              <button
                type="button"
                onClick={() => setRating(1)}
                className={`group p-3 rounded-xl transition-all duration-300 ${
                  rating === 1 
                    ? 'bg-gradient-to-r from-red-500/20 to-red-600/20 scale-110 shadow-lg shadow-red-500/20' 
                    : 'hover:bg-slate-800/50'
                }`}
                aria-label="Not helpful"
                aria-pressed={rating === 1}
              >
                <ThumbsDown className={`w-8 h-8 transition-colors ${
                  rating === 1 
                    ? 'text-red-400' 
                    : 'text-slate-400 group-hover:text-red-400'
                }`} />
              </button>
              <button
                type="button"
                onClick={() => setRating(5)}
                className={`group p-3 rounded-xl transition-all duration-300 ${
                  rating === 5 
                    ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 scale-110 shadow-lg shadow-emerald-500/20' 
                    : 'hover:bg-slate-800/50'
                }`}
                aria-label="Very helpful"
                aria-pressed={rating === 5}
              >
                <ThumbsUp className={`w-8 h-8 transition-colors ${
                  rating === 5 
                    ? 'text-emerald-400' 
                    : 'text-slate-400 group-hover:text-emerald-400'
                }`} />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="feedback-comment" className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">
              Additional comments (optional)
            </label>
            <textarea
              ref={commentRef}
              id="feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all min-h-[100px] resize-none"
              placeholder="What could be improved? What was helpful?"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                onSubmit(rating, comment);
                onClose();
              }}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg px-6 py-2 text-sm font-medium transition-all duration-300 shadow-lg shadow-blue-500/20 hover:shadow-indigo-500/20"
            >
              Submit Feedback
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;